import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Map, Truck, LayoutDashboard, Bell, Menu, ClipboardList, Route, PackageCheck, Shield } from 'lucide-react';
import { useHasModule } from '../store/modules';
import { useNotificationsStore } from '../store/notifications.store';
import { useAuthStore } from '../store/auth.store';
import clsx from 'clsx';

interface BottomNavProps {
  onMenuClick: () => void;
}

type Tab = {
  /** Stable key matching the per-role mobile-nav config (MOBILE_NAV_TABS). */
  key: string;
  /** i18n key resolved with t() at render time. */
  labelKey: string;
  href: string;
  icon: typeof Map;
  showBadge?: boolean;
  /** Tenant permission key required to enable this tab. Tabs without permission
   *  are still rendered but greyed out and non-clickable. */
  permission?: string;
  /** Add-on module key — tab is hidden entirely when the module is not active. */
  module?: 'VRP' | 'EV' | 'FIELD_SERVICE' | 'POD';
};

const tabs: Tab[] = [
  { key: 'my-missions', labelKey: 'bottom_nav.missions', href: '/my-missions', icon: ClipboardList, permission: 'missions', module: 'FIELD_SERVICE' },
  { key: 'my-parcels', labelKey: 'bottom_nav.parcels', href: '/my-parcels', icon: PackageCheck, permission: 'parcels', module: 'POD' },
  { key: 'my-tours', labelKey: 'bottom_nav.tours', href: '/my-tours', icon: Route, permission: 'vrp', module: 'VRP' },
  { key: 'map', labelKey: 'bottom_nav.map', href: '/map', icon: Map, permission: 'map' },
  { key: 'devices', labelKey: 'bottom_nav.devices', href: '/devices', icon: Truck, permission: 'devices' },
  { key: 'dashboard', labelKey: 'bottom_nav.dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboard' },
  { key: 'alerts', labelKey: 'bottom_nav.alerts', href: '/alerts', icon: Bell, showBadge: true, permission: 'alerts' },
  // Deliberately has no `permission`: a driver's own privacy screen is not a
  // fleet feature they can be denied. The DRIVER role grants only
  // missions/parcels/vrp, so gating this on `settings` would hide it from
  // exactly the people it exists for — they had no way to reach /settings at
  // all before this entry.
  { key: 'privacy', labelKey: 'bottom_nav.privacy', href: '/settings?tab=privacy', icon: Shield },
];

export default function BottomNav({ onMenuClick }: BottomNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const unreadCount = useNotificationsStore((state) => state.unreadCount);
  const user = useAuthStore((s) => s.user);
  const hasFieldService = useHasModule('FIELD_SERVICE');
  const hasVrp = useHasModule('VRP');
  const hasPod = useHasModule('POD');

  // Filter out tabs whose required add-on module isn't enabled for the tenant,
  // then apply the per-role mobile-nav config (resolved server-side in
  // user.mobileNavTabs). When that hasn't loaded yet (e.g. a session cached
  // before this feature), fall back to showing all module-enabled tabs to
  // avoid a flash of missing tabs — /auth/me hydrates it shortly after.
  const navTabs = user?.mobileNavTabs;
  const visibleTabs = tabs.filter((t) => {
    if (t.module === 'FIELD_SERVICE' && !hasFieldService) return false;
    if (t.module === 'VRP' && !hasVrp) return false;
    if (t.module === 'POD' && !hasPod) return false;
    if (navTabs) return navTabs.includes(t.key);
    return true;
  });

  // Compute whether the current user has at least one grant for a given permission key.
  // SUPER_ADMIN and tenant ADMIN always have full access. For other roles we look at
  // tenantPermissions (loaded from /auth/me at login). When permissions are not yet
  // loaded we default to "allowed" to avoid a flash of greyed-out tabs.
  const hasPermission = (permission?: string): boolean => {
    if (!permission) return true;
    if (!user) return false;
    if (user.role === 'SUPER_ADMIN' || user.role === 'ADMIN') return true;
    const perms = user.tenantPermissions;
    if (!perms) return true;
    return perms.some((p) => p.split(':')[0] === permission);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 glass-header border-t border-gray-200 pb-safe md:hidden">
      <div className="flex items-center justify-around h-14">
        {visibleTabs.map((tab) => {
          const allowed = hasPermission(tab.permission);
          const label = t(tab.labelKey);
          // Compared on pathname alone elsewhere, but the privacy entry carries
          // a query string, so it needs its own check.
          const isActive = location.pathname === tab.href ||
            (tab.key === 'privacy' && location.pathname === '/settings') ||
            (tab.href === '/alerts' && location.pathname.startsWith('/alerts')) ||
            (tab.href === '/my-missions' && location.pathname.startsWith('/my-missions')) ||
            (tab.href === '/my-parcels' && location.pathname.startsWith('/my-parcels')) ||
            (tab.href === '/my-tours' && location.pathname.startsWith('/my-tours'));

          if (!allowed) {
            return (
              <div
                key={tab.labelKey}
                aria-disabled="true"
                className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-gray-300 opacity-50 cursor-not-allowed select-none"
                title={t('bottom_nav.access_denied', { name: label })}
              >
                <tab.icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{label}</span>
              </div>
            );
          }

          return (
            <Link
              key={tab.labelKey}
              to={tab.href}
              className={clsx(
                'flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors relative',
                isActive ? 'text-primary-600' : 'text-gray-400',
              )}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-[10px] font-medium">{label}</span>
              {tab.showBadge && unreadCount > 0 && (
                <span className="absolute top-1.5 right-1/2 translate-x-3 min-w-[16px] h-4 flex items-center justify-center px-1 text-[9px] font-bold bg-red-500 text-white rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          );
        })}
        <button
          onClick={onMenuClick}
          className="flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-gray-400 transition-colors"
        >
          <Menu className="h-5 w-5" />
          <span className="text-[10px] font-medium">{t('bottom_nav.more')}</span>
        </button>
      </div>
    </nav>
  );
}
